/**
 * Checkout Cancel Page
 *
 * Shown when user cancels Stripe checkout.
 * Provides options to return to cart or continue shopping.
 */

import React from 'react';
import { useCart } from '../contexts/CartContext';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import navigationService from '../services/navigationService';
import './CheckoutCancel.css';

const CheckoutCancel = () => {
  const { items, itemCount } = useCart();

  const handleReturnToCart = () => {
    // Cart will automatically open when user clicks the cart icon
    navigationService.navigateTo('home');
    // User can manually open cart from header
  };

  const handleContinueShopping = () => {
    navigationService.navigateTo('home');
  };

  const handleViewSubscriptions = () => {
    navigationService.navigateTo('subscription');
  };

  return (
    <>
      <MainHeader currentPage="checkout-cancel" />
      <div className="checkout-cancel">
        <div className="cancel-container">
          {/* Cancel Icon */}
          <div className="cancel-icon">
            <MaterialIcon icon="cancel" size={80} />
          </div>

          {/* Cancel Message */}
          <h1 className="cancel-title">Checkout Canceled</h1>
          <p className="cancel-subtitle">
            No worries! Your {itemCount > 0 ? 'cart items are' : 'session'} still saved.
          </p>

          {/* Cart Summary (if items exist) */}
          {itemCount > 0 && (
            <div className="cancel-cart-summary">
              <div className="cancel-cart-header">
                <MaterialIcon icon="shopping_cart" />
                <h2>
                  Your cart has {itemCount} item{itemCount !== 1 ? 's' : ''}
                </h2>
              </div>
              <p className="cancel-cart-note">
                Items remain in your cart until you're ready to checkout.
              </p>
            </div>
          )}

          {/* Why Checkout Failed */}
          <div className="cancel-details">
            <h2>Common Reasons for Cancellation</h2>
            <div className="cancel-reasons">
              <div className="cancel-reason">
                <MaterialIcon icon="credit_card" />
                <p>Payment method declined or expired</p>
              </div>
              <div className="cancel-reason">
                <MaterialIcon icon="close" />
                <p>Accidentally closed the checkout window</p>
              </div>
              <div className="cancel-reason">
                <MaterialIcon icon="hourglass_empty" />
                <p>Session timed out</p>
              </div>
              <div className="cancel-reason">
                <MaterialIcon icon="psychology" />
                <p>Changed your mind (that's okay!)</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="cancel-actions">
            {itemCount > 0 ? (
              <>
                <button className="cancel-button-primary" onClick={handleReturnToCart}>
                  <MaterialIcon icon="shopping_cart" />
                  View My Cart
                </button>
                <button className="cancel-button-secondary" onClick={handleContinueShopping}>
                  <MaterialIcon icon="storefront" />
                  Continue Shopping
                </button>
              </>
            ) : (
              <>
                <button className="cancel-button-primary" onClick={handleContinueShopping}>
                  <MaterialIcon icon="storefront" />
                  Continue Shopping
                </button>
                <button className="cancel-button-secondary" onClick={handleViewSubscriptions}>
                  <MaterialIcon icon="card_membership" />
                  View Subscriptions
                </button>
              </>
            )}
          </div>

          {/* Help Section */}
          <div className="cancel-help">
            <MaterialIcon icon="help" />
            <div className="cancel-help-content">
              <h3>Need Help?</h3>
              <p>
                If you're experiencing issues with checkout, please contact us at{' '}
                <a href="mailto:support@clothedeye.com">support@clothedeye.com</a>
              </p>
            </div>
          </div>

          {/* Alternative Payment Info */}
          <div className="cancel-alternative">
            <MaterialIcon icon="info" />
            <p>
              You can also purchase our stickers on{' '}
              <a
                href="https://www.etsy.com/shop/ClothedEye"
                target="_blank"
                rel="noopener noreferrer"
              >
                Etsy
              </a>{' '}
              if you prefer
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutCancel;
